import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { toast } from "react-hot-toast";
import {
  FiUsers,
  FiUser,
  FiPlus,
  FiUpload,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiX,
  FiFile,
  FiDownload,
  FiEdit,
  FiSearch,
  FiFilter,
  FiMail,
  FiPhone,
  FiGrid,
  FiList,
} from "react-icons/fi";
import {
  fetchSurveyOfficers,
  createSurveyOfficer,
  uploadSurveyOfficers,
  downloadTemplate,
  updateSurveyOfficer,
} from "../../redux/actions/user-management/surveyOfficerActions";
import {
  fetchDistricts,
  fetchMunicipalities,
  fetchWards,
  clearMunicipalities,
  clearWards,
} from "../../redux/actions/internal/internalActions";

const SurveyOfficers = () => {
  const dispatch = useDispatch();
  const { loading, surveyOfficers, pagination, uploadProgress } = useSelector(
    (state) =>
      state.surveyOfficer || {
        loading: false,
        surveyOfficers: [],
        pagination: { page: 1, limit: 10, totalRecords: 0, totalPages: 0 },
        uploadProgress: 0,
      },
  );
  const { districts, municipalities, wards } = useSelector(
    (state) =>
      state.internal || { districts: [], municipalities: [], wards: [] },
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [filters, setFilters] = useState({
    district_id: "",
    municipality_id: "",
    // search: "",
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [limit, setLimit] = useState(10);
  const limitOptions = [
    10, 20, 50, 100, 200, 500, 1000, 1500, 2000, 3000, 4000,
  ];
  const [phoneError, setPhoneError] = useState("");
  const [editPhoneError, setEditPhoneError] = useState("");
  const [editId, setEditId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'grid'

  const toggleWardExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    email: "",
    phone: "",
    district_id: "",
    municipality_id: "",
    ward_ids: [],
  });

  const [editData, setEditData] = useState({
    name: "",
    designation: "",
    email: "",
    phone: "",
    district_id: "",
    municipality_id: "",
    ward_ids: [],
    role_id: "",
  });

  const handleDownloadTemplate = () => {
    dispatch(downloadTemplate());
  };

  useEffect(() => {
    dispatch(fetchSurveyOfficers(currentPage, limit, filters));
    dispatch(fetchDistricts());
  }, [
    dispatch,
    currentPage,
    limit,
    filters.district_id,
    filters.municipality_id,
    // filters.search,
  ]);

  useEffect(() => {
    if (formData.district_id) {
      dispatch(fetchMunicipalities(formData.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [formData.district_id, dispatch]);

  useEffect(() => {
    if (filters.district_id) {
      dispatch(fetchMunicipalities(filters.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [filters.district_id, dispatch]);

  useEffect(() => {
    if (formData.municipality_id) {
      dispatch(fetchWards(formData.municipality_id));
    } else {
      dispatch(clearWards());
    }
  }, [formData.municipality_id, dispatch]);

  useEffect(() => {
    if (editData.municipality_id) {
      dispatch(fetchWards(editData.municipality_id));
    } else {
      dispatch(clearWards());
    }
  }, [editData.municipality_id, dispatch]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/v1/api/get-role-list`,
          { headers: { authorization: token } },
        );
        if (response.data.meta.status) {
          setRoles(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch roles:", error);
      }
    };
    fetchRoles();
  }, []);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      dispatch(fetchSurveyOfficers(page, limit, filters));
    }
  };

  const handleRefresh = () => {
    dispatch(fetchSurveyOfficers(currentPage, limit, filters));
    toast.success("Data refreshed!");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleWardSelect = (wardId) => {
    setFormData((prev) => ({
      ...prev,
      ward_ids: prev.ward_ids.includes(wardId)
        ? prev.ward_ids.filter((id) => id !== wardId)
        : [...prev.ward_ids, wardId],
    }));
  };

  const isValidPhone = (phone) => {
    return phone.length === 10;
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!isValidPhone(formData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }
    const submitData = {
      ...formData,
      district_id: parseInt(formData.district_id),
      municipality_id: parseInt(formData.municipality_id),
    };

    try {
      const result = await dispatch(createSurveyOfficer(submitData));
      if (result?.meta?.status) {
        setShowAddModal(false);
        setFormData({
          name: "",
          designation: "",
          email: "",
          phone: "",
          district_id: "",
          municipality_id: "",
          ward_ids: [],
        });
        setPhoneError("");
        dispatch(fetchSurveyOfficers(currentPage, limit, filters));
      }
    } catch (error) {
      // Error handled in action
    }
  };

  const handleEdit = (officer) => {
    setEditId(officer.id);
    setEditData({
      name: officer.name || "",
      designation: officer.designation || "",
      email: officer.email || "",
      phone: officer.phone || "",
      district_id: officer.district_id || "",
      municipality_id: officer.municipality_id || "",
      ward_ids: officer.ward_ids || [],
      role_id: officer.role_id || "",
    });
    if (officer.district_id) {
      dispatch(fetchMunicipalities(officer.district_id));
    }
    if (officer.municipality_id) {
      dispatch(fetchWards(officer.municipality_id));
    }
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!isValidPhone(editData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }
    const submitData = {
      ...editData,
      district_id: parseInt(editData.district_id),
      municipality_id: parseInt(editData.municipality_id),
    };

    try {
      const result = await dispatch(updateSurveyOfficer(editId, submitData));
      if (result?.meta?.status) {
        setShowEditModal(false);
        setEditData({
          name: "",
          designation: "",
          email: "",
          phone: "",
          district_id: "",
          municipality_id: "",
          ward_ids: [],
          role_id: "",
        });
        setEditPhoneError("");
        dispatch(fetchSurveyOfficers(currentPage, limit, filters));
      }
    } catch (error) {}
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditData({ ...editData, [name]: value });
  };

  const handleEditWardSelect = (wardId) => {
    setEditData((prev) => ({
      ...prev,
      ward_ids: prev.ward_ids.includes(wardId)
        ? prev.ward_ids.filter((id) => id !== wardId)
        : [...prev.ward_ids, wardId],
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const result = await dispatch(uploadSurveyOfficers(formData));
      if (result?.meta?.status) {
        setSelectedFile(null);
        document.getElementById("uploadFileInput").value = "";
        setShowUploadModal(false);
        dispatch(fetchSurveyOfficers(currentPage, limit, filters));
      }
    } catch (error) {
      // Error handled in action
    } finally {
      setIsUploading(false);
    }
  };
  const handleExportToExcel = async () => {
    try {
      const result = await dispatch(fetchSurveyOfficers(1, 99999, filters));

      if (!result?.data || result.data.length === 0) {
        toast.error("No data to export");
        return;
      }

      const allOfficers = result.data;

      const headers = [
        "S.N.",
        "Full Name",
        "Designation",
        "Email",
        "Phone",
        "Role",
        "District",
        "Municipality",
        "Wards",
      ];

      const rows = allOfficers.map((item, index) => ({
        "S.N.": index + 1,
        "Full Name": item.name || "N/A",
        Designation: item.designation || "N/A",
        Email: item.email || "N/A",
        Phone: item.phone || "N/A",
        Role:
          roles.find((role) => role.id === item.role_id)?.role_name || "N/A",
        District: item.district_name || "N/A",
        Municipality: item.municipality_name || "N/A",
        Wards: item.ward_names || "N/A",
      }));

      const data = [headers, [], ...rows.map((row) => Object.values(row))];
      const ws = XLSX.utils.aoa_to_sheet(data);

      const colWidths = [
        { wch: 6 },
        { wch: 25 },
        { wch: 20 },
        { wch: 30 },
        { wch: 15 },
        { wch: 20 },
        { wch: 25 },
        { wch: 25 },
        { wch: 30 },
      ];
      ws["!cols"] = colWidths;

      const range = XLSX.utils.decode_range(ws["!ref"]);
      for (let R = range.s.r; R <= range.e.r; R++) {
        for (let C = range.s.c; C <= range.e.c; C++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[addr]) continue;
          if (!ws[addr].s) ws[addr].s = {};

          if (R === 0) {
            ws[addr].s = {
              fill: { fgColor: { rgb: "FFCC6633" } },
              font: { color: { rgb: "FFFFFFFF" }, bold: true, sz: 12 },
              alignment: { horizontal: "center", vertical: "center" },
              border: {
                top: { style: "thin", color: { rgb: "FF999999" } },
                bottom: { style: "thin", color: { rgb: "FF999999" } },
                left: { style: "thin", color: { rgb: "FF999999" } },
                right: { style: "thin", color: { rgb: "FF999999" } },
              },
            };
          } else if (R > 1 && R % 2 === 0) {
            ws[addr].s = {
              fill: { fgColor: { rgb: "FFF0F8FF" } },
              alignment: { horizontal: "left", vertical: "center" },
              font: { sz: 11, color: { rgb: "FF333333" } },
              border: {
                top: { style: "thin", color: { rgb: "FFD0D0D0" } },
                bottom: { style: "thin", color: { rgb: "FFD0D0D0" } },
                left: { style: "thin", color: { rgb: "FFD0D0D0" } },
                right: { style: "thin", color: { rgb: "FFD0D0D0" } },
              },
            };
          } else if (R > 1 && R % 2 !== 0) {
            ws[addr].s = {
              fill: { fgColor: { rgb: "FFFFFFFF" } },
              alignment: { horizontal: "left", vertical: "center" },
              font: { sz: 11, color: { rgb: "FF333333" } },
              border: {
                top: { style: "thin", color: { rgb: "FFD0D0D0" } },
                bottom: { style: "thin", color: { rgb: "FFD0D0D0" } },
                left: { style: "thin", color: { rgb: "FFD0D0D0" } },
                right: { style: "thin", color: { rgb: "FFD0D0D0" } },
              },
            };
          }
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "SurveyOfficers");

      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
      XLSX.writeFile(wb, `Survey_Officers_${dateStr}.xlsx`);

      toast.success(`Exported ${allOfficers.length} records successfully!`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-6 md:p-8">
      {/* Animated Background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-200/30 dark:bg-orange-900/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200/30 dark:bg-blue-900/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Header with Glassmorphism */}
      <div className="relative backdrop-blur-xl bg-white/70 dark:bg-slate-800/70 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-4 md:p-5 mb-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg">
              <FiUsers className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-600 to-orange-400 bg-clip-text text-transparent">
                Survey Officers
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Manage and monitor survey officers efficiently
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base"
            >
              <FiUpload className="w-5 h-5" />
              <span className="hidden sm:inline">Upload</span>
            </button>
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-slate-200 dark:border-slate-600 text-base"
            >
              <FiRefreshCw className="w-5 h-5" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={handleExportToExcel}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base"
            >
              <FiDownload className="w-5 h-5" />
              <span className="hidden sm:inline">Export</span>
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-base"
            >
              <FiPlus className="w-5 h-5" />
              <span>Add Officer</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {[
          {
            label: "Total Officers",
            value: pagination.totalRecords || 0,
            icon: FiUsers,
            bgColor: "bg-orange-50 dark:bg-orange-900/20",
            borderColor: "border-orange-200 dark:border-orange-800",
            iconBg: "from-orange-500 to-orange-600",
          },
          {
            label: "Districts",
            value: new Set(surveyOfficers.map((o) => o.district_id)).size,
            icon: FiUsers,
            bgColor: "bg-blue-50 dark:bg-blue-900/20",
            borderColor: "border-blue-200 dark:border-blue-800",
            iconBg: "from-blue-500 to-blue-600",
          },
          {
            label: "Municipalities",
            value: new Set(surveyOfficers.map((o) => o.municipality_id)).size,
            icon: FiUsers,
            bgColor: "bg-purple-50 dark:bg-purple-900/20",
            borderColor: "border-purple-200 dark:border-purple-800",
            iconBg: "from-purple-500 to-purple-600",
          },
          {
            label: "Total Wards",
            value: surveyOfficers.reduce(
              (acc, o) => acc + (o.ward_ids?.length || 0),
              0,
            ),
            icon: FiUsers,
            bgColor: "bg-emerald-50 dark:bg-emerald-900/20",
            borderColor: "border-emerald-200 dark:border-emerald-800",
            iconBg: "from-emerald-500 to-emerald-600",
          },
        ].map((stat, idx) => (
          <div
            key={idx}
            className={`${stat.bgColor} border ${stat.borderColor} rounded-xl shadow-sm p-3 hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  {stat.label}
                </p>
                <p className="text-2xl font-bold text-slate-800 dark:text-white mt-0.5">
                  {stat.value}
                </p>
              </div>
              <div
                className={`p-2.5 bg-gradient-to-br ${stat.iconBg} rounded-xl shadow-lg`}
              >
                <stat.icon className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content with Glassmorphism */}
      <div className="relative backdrop-blur-xl bg-white/70 dark:bg-slate-800/70 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-6">
        {/* Filters and Controls */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 mb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <select
                value={filters.district_id}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilters({
                    district_id: value,
                    municipality_id: "",
                  });
                  setCurrentPage(1);
                }}
                className="px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white w-full sm:w-auto"
              >
                <option value="">All Districts</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
              <select
                value={filters.municipality_id}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    municipality_id: e.target.value,
                  });
                  setCurrentPage(1);
                }}
                disabled={!filters.district_id}
                className="px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white disabled:opacity-50 w-full sm:w-auto min-w-[180px]"
              >
                <option value="">All Municipalities</option>
                {municipalities.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>

              {/* Clear Filter Button*/}
              {(filters.district_id || filters.municipality_id) && (
                <button
                  onClick={() => {
                    setFilters({
                      district_id: "",
                      municipality_id: "",
                    });
                    setCurrentPage(1);
                    dispatch(clearMunicipalities());
                  }}
                  className="flex items-center gap-1.5 px-3 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 text-sm font-medium whitespace-nowrap"
                >
                  <FiX className="w-4 h-4" />
                  Clear Filters
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="flex items-center gap-2 bg-white dark:bg-slate-700 rounded-xl border border-slate-200 dark:border-slate-600 p-1">
              <button
                onClick={() => setViewMode("table")}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === "table"
                    ? "bg-orange-500 text-white shadow-md"
                    : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                }`}
              >
                <FiList className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === "grid"
                    ? "bg-orange-500 text-white shadow-md"
                    : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                }`}
              >
                <FiGrid className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600 dark:text-slate-400">
                Show:
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-slate-700 dark:text-white"
              >
                {limitOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-orange-200 dark:border-orange-800 rounded-full"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        ) : surveyOfficers.length > 0 ? (
          <>
            {viewMode === "table" ? (
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        #
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Designation
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Wards
                      </th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {surveyOfficers.map((item, index) => (
                      <tr
                        key={item.id}
                        className="hover:bg-orange-50/50 dark:hover:bg-orange-900/20 transition-colors duration-150"
                      >
                        <td className="py-3 px-4 text-sm text-slate-500 dark:text-slate-400">
                          {(pagination.page - 1) * limit + index + 1}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white">
                              <FiUser className="w-4 h-4" />
                            </div>
                            <span className="text-sm font-medium text-slate-800 dark:text-white">
                              {item.name || "N/A"}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                          {item.designation || "N/A"}
                        </td>
                        <td className="py-3 px-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                              <FiMail className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                              {item.email || "N/A"}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                              <FiPhone className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                              {item.phone || "N/A"}
                            </div>
                          </div>
                        </td>

                        <td className="inline-block px-2.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-medium">
                          {roles.find((role) => role.id === item.role_id)
                            ?.role_name || "N/A"}
                        </td>
                        <td className="py-3 px-4">
                          <div className="space-y-1">
                            <div className="text-sm text-slate-600 dark:text-slate-300">
                              {item.district_name || "N/A"}
                            </div>
                            <div className="text-sm text-slate-500 dark:text-slate-400">
                              {item.municipality_name || "N/A"}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                          {item.ward_names
                            ? (() => {
                                const wardList = item.ward_names
                                  .split(",")
                                  .map((w) => w.trim());
                                const isExpanded = expandedRows[item.id];
                                const displayWards = isExpanded
                                  ? wardList
                                  : wardList.slice(0, 2);

                                return (
                                  <div>
                                    <span className="font-mono text-xs">
                                      {displayWards.join(", ")}
                                      {wardList.length > 2 &&
                                        !isExpanded &&
                                        "..."}
                                    </span>
                                    {wardList.length > 2 && (
                                      <button
                                        onClick={() =>
                                          toggleWardExpand(item.id)
                                        }
                                        className="ml-1 text-xs font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 transition-colors"
                                      >
                                        {isExpanded
                                          ? "Show less"
                                          : `+${wardList.length - 2} more`}
                                      </button>
                                    )}
                                  </div>
                                );
                              })()
                            : "N/A"}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 text-orange-600 hover:bg-orange-100 dark:text-orange-400 dark:hover:bg-orange-900/30 rounded-lg transition-all duration-200 hover:scale-110"
                            title="Edit Survey Officer"
                          >
                            <FiEdit className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              // Grid View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {surveyOfficers.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white dark:bg-slate-700/50 rounded-xl shadow-lg border border-slate-200 dark:border-slate-600 p-4 hover:shadow-xl transition-all duration-300 transform hover:scale-102"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white text-lg font-medium">
                          {item.name?.charAt(0) || "N"}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800 dark:text-white">
                            {item.name || "N/A"}
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {item.designation || "N/A"}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 text-orange-600 hover:bg-orange-100 dark:text-orange-400 dark:hover:bg-orange-900/30 rounded-lg transition-all duration-200 hover:scale-110"
                      >
                        <FiEdit className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-500 dark:text-slate-400">
                          Email:
                        </span>
                        <span className="text-slate-700 dark:text-slate-300">
                          {item.email || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-500 dark:text-slate-400">
                          Phone:
                        </span>
                        <span className="text-slate-700 dark:text-slate-300">
                          {item.phone || "N/A"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-500 dark:text-slate-400">
                          Location:
                        </span>
                        <span className="text-slate-700 dark:text-slate-300">
                          {item.district_name || "N/A"} -{" "}
                          {item.municipality_name || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-6 border-t border-slate-200 dark:border-slate-700 gap-4">
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.totalRecords,
                  )}{" "}
                  of {pagination.totalRecords} entries
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2.5 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-slate-600 dark:text-slate-300"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-medium min-w-[40px] text-center">
                    {currentPage}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2.5 rounded-xl bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-slate-600 dark:text-slate-300"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiUsers className="w-12 h-12 text-slate-300 dark:text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">
              No survey officers found
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Get started by adding your first survey officer
            </p>
          </div>
        )}
      </div>

      {/* Modals remain largely the same but with updated styles */}
      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto animate-fadeIn">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                Add Survey Officer
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setPhoneError("");
                  setFormData({
                    name: "",
                    designation: "",
                    email: "",
                    phone: "",
                    district_id: "",
                    municipality_id: "",
                    ward_ids: [],
                  });
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              {/* Form fields with updated styling */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Designation *
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      setFormData({ ...formData, phone: value });
                      if (value.length > 0 && value.length !== 10) {
                        setPhoneError("Phone number must be exactly 10 digits");
                      } else {
                        setPhoneError("");
                      }
                    }}
                    required
                    className={`w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200 ${
                      phoneError
                        ? "border-red-500 dark:border-red-500"
                        : "border-slate-200 dark:border-slate-600"
                    }`}
                    placeholder="Enter 10-digit phone number"
                  />
                  {phoneError && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {phoneError}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    District *
                  </label>
                  <select
                    name="district_id"
                    value={formData.district_id}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Municipality *
                  </label>
                  <select
                    name="municipality_id"
                    value={formData.municipality_id}
                    onChange={handleInputChange}
                    required
                    disabled={!formData.district_id}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white disabled:opacity-50 transition-all duration-200"
                  >
                    <option value="">Select Municipality</option>
                    {municipalities.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Wards *
                </label>
                <div className="border border-slate-200 dark:border-slate-600 rounded-xl p-4 max-h-40 overflow-y-auto bg-slate-50 dark:bg-slate-700/50">
                  {wards.length > 0 ? (
                    <div className="grid grid-cols-4 gap-2">
                      {wards.map((ward) => (
                        <label
                          key={ward.id}
                          className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 px-2 rounded-lg transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={formData.ward_ids.includes(ward.id)}
                            onChange={() => handleWardSelect(ward.id)}
                            disabled={!formData.municipality_id}
                            className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-slate-300 rounded"
                          />
                          <span className="text-sm text-slate-700 dark:text-slate-300">
                            {ward.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-4">
                      {!formData.municipality_id
                        ? "Select municipality first"
                        : "No wards available"}
                    </p>
                  )}
                </div>
                {formData.ward_ids.length > 0 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    Selected: {formData.ward_ids.length} ward(s)
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setPhoneError("");
                    setFormData({
                      name: "",
                      designation: "",
                      email: "",
                      phone: "",
                      district_id: "",
                      municipality_id: "",
                      ward_ids: [],
                    });
                  }}
                  className="px-4 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  Create Officer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal with updated styling */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl animate-fadeIn">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                Upload Survey Officers
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  document.getElementById("uploadFileInput").value = "";
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 text-center hover:border-orange-500 dark:hover:border-orange-400 transition-all duration-300 bg-slate-50 dark:bg-slate-700/30">
              <input
                id="uploadFileInput"
                type="file"
                onChange={handleFileChange}
                className="hidden"
                disabled={isUploading || loading}
              />
              <label htmlFor="uploadFileInput" className="cursor-pointer block">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mb-4">
                    <FiFile className="w-8 h-8 text-orange-500 dark:text-orange-400" />
                  </div>
                  <p className="text-slate-600 dark:text-slate-300 font-medium">
                    {selectedFile ? selectedFile.name : "Click to select file"}
                  </p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                    Supported formats: .xlsx, .xls, .csv
                  </p>
                  {selectedFile && (
                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-2">
                      Size: {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  )}
                </div>
              </label>
            </div>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-6">
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                  {uploadProgress}% uploaded
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  document.getElementById("uploadFileInput").value = "";
                }}
                className="px-4 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadSubmit}
                disabled={!selectedFile || isUploading || loading}
                className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                    Uploading...
                  </>
                ) : (
                  "Upload"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal with updated styling */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto animate-fadeIn">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                Edit Survey Officer
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditPhoneError("");
                  setEditData({
                    name: "",
                    designation: "",
                    email: "",
                    phone: "",
                    district_id: "",
                    municipality_id: "",
                    ward_ids: [],
                  });
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              {/* Similar form fields as add modal */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={editData.name}
                    onChange={handleEditInputChange}
                    required
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Designation *
                  </label>
                  <input
                    type="text"
                    name="designation"
                    value={editData.designation}
                    onChange={handleEditInputChange}
                    required
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={editData.email}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={editData.phone}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      setEditData({ ...editData, phone: value });
                      if (value.length > 0 && value.length !== 10) {
                        setEditPhoneError(
                          "Phone number must be exactly 10 digits",
                        );
                      } else {
                        setEditPhoneError("");
                      }
                    }}
                    required
                    className={`w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200 ${
                      editPhoneError
                        ? "border-red-500 dark:border-red-500"
                        : "border-slate-200 dark:border-slate-600"
                    }`}
                    placeholder="Enter 10-digit phone number"
                  />
                  {editPhoneError && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {editPhoneError}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    District *
                  </label>
                  <select
                    name="district_id"
                    value={editData.district_id}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditData({
                        ...editData,
                        district_id: value,
                        municipality_id: "",
                        ward_ids: [],
                      });
                      if (value) {
                        dispatch(fetchMunicipalities(value));
                      } else {
                        dispatch(clearMunicipalities());
                      }
                    }}
                    required
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white transition-all duration-200"
                  >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Municipality *
                  </label>
                  <select
                    name="municipality_id"
                    value={editData.municipality_id}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditData({
                        ...editData,
                        municipality_id: value,
                        ward_ids: [],
                      });
                      if (value) {
                        dispatch(fetchWards(value));
                      } else {
                        dispatch(clearWards());
                      }
                    }}
                    required
                    disabled={!editData.district_id}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-700 dark:text-white disabled:opacity-50 transition-all duration-200"
                  >
                    <option value="">Select Municipality</option>
                    {municipalities.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Wards *
                </label>
                <div className="border border-slate-200 dark:border-slate-600 rounded-xl p-4 max-h-40 overflow-y-auto bg-slate-50 dark:bg-slate-700/50">
                  {wards.length > 0 ? (
                    <div className="grid grid-cols-4 gap-2">
                      {wards.map((ward) => (
                        <label
                          key={ward.id}
                          className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 px-2 rounded-lg transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={editData.ward_ids.includes(ward.id)}
                            onChange={() => handleEditWardSelect(ward.id)}
                            disabled={!editData.municipality_id}
                            className="w-4 h-4 text-orange-600 focus:ring-orange-500 border-slate-300 rounded"
                          />
                          <span className="text-sm text-slate-700 dark:text-slate-300">
                            {ward.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-4">
                      {!editData.municipality_id
                        ? "Select municipality first"
                        : "No wards available"}
                    </p>
                  )}
                </div>
                {editData.ward_ids.length > 0 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    Selected: {editData.ward_ids.length} ward(s)
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditPhoneError("");
                    setEditData({
                      name: "",
                      designation: "",
                      email: "",
                      phone: "",
                      district_id: "",
                      municipality_id: "",
                      ward_ids: [],
                    });
                  }}
                  className="px-4 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  Update Officer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyOfficers;
